import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Button, Divider, Grid, Typography } from '@mui/material'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import { useHistory } from 'react-router-dom'
import {
  filterByCompanyID,
  filterBySummary,
  filterByUserType,
  retrieveUsers,
  searchUsers,
  usersPagination,
  usersSortBy,
} from '../../../redux/features/users/sysAdmin/sysAdminUsersThunk'
import { retrieveCompaniesList } from '../../../redux/features/companies/companiesThunk'
import { selectCompanies } from '../../../redux/features/companies/companiesSlice'
import SearchBar from '../../../components/PageHelpers/SearchBar.jsx'
import SortByList from '../../../components/PageHelpers/Sort.jsx'
import OfoqThemeBtn from '../../../components/Buttons/OfoqThemeBtn.jsx'
import FilterView from '../../../components/Buttons/Filter.jsx'
import Loader from '../../../components/helpers/Loader.jsx'
import OfoqAutoComplete from '../../../components/Forms/OfoqAutoComplete.jsx'
import AppRoutes from '../../../services/AppRoutes'
import TitleWithCount from '../../../components/PageHelpers/TitleWithCount.jsx'
import SummariesBtn from '../../../components/PageHelpers/SummariesBtn.jsx'
import LoadMore from '../../../components/Buttons/LoadMore.jsx'
import SysAdminUsersCard from '../../../components/Cards/SysAdminUsersCard.jsx'
import CookiesStorageService from '../../../services/CookiesStorageService'
import { isNotEmpty } from '../../../utils/helpers'
import { sysAdminSelectUsers } from '../../../redux/features/users/sysAdmin/sysAdminUsersSlice'

const Users = () => {
  const history = useHistory()

  const baseUri = process.env.REACT_APP_ROOT_ROUTE || ''

  const dispatch = useDispatch()
  const users = useSelector(sysAdminSelectUsers)
  const companies = useSelector(selectCompanies)

  useEffect(() => {
    dispatch(retrieveUsers())
    dispatch(retrieveCompaniesList())
  }, [])

  const [filter, setFilter] = useState(false)
  const [clearFilter, setClearFilter] = useState(false)
  const [filterUserType, setFilterUserType] = useState([])
  const [filterUserCompany, setFilterUserCompany] = useState([])

  const handleFilterChange = (event) => {
    const queries = isNotEmpty(event) ? event.map((item) => item.value) : []
    dispatch(filterByUserType({ queries }))
  }

  const handleCompanyFilter = (event) => {
    const queries = isNotEmpty(event) ? event.map((item) => item.id) : []
    dispatch(filterByCompanyID({ queries }))
  }

  const handleNavigation = () => {
    history.push(`${baseUri}${AppRoutes.adduser}`)
  }

  const handleClearFilter = () => {
    setFilterUserType([])
    setFilterUserCompany([])
    dispatch(retrieveUsers())
    setClearFilter(false)
    setFilter(false)
  }

  const handleRemoveClear = () => {
    if (!isNotEmpty(filterUserType) && !isNotEmpty(filterUserCompany)) setClearFilter(false)
  }

  useEffect(() => {
    handleFilterChange(filterUserType)
    if (isNotEmpty(filterUserType)) setClearFilter(true)
    else if (clearFilter) handleRemoveClear()
  }, [filterUserType])

  useEffect(() => {
    handleCompanyFilter(filterUserCompany)
    if (isNotEmpty(filterUserCompany)) setClearFilter(true)
    else if (clearFilter) handleRemoveClear()
  }, [filterUserCompany])

  const username = CookiesStorageService.getUserName()

  return (
    <Box sx={styles.container}>
      <Box sx={styles.titleBox}>
        <Typography sx={styles.welcomeHeader}>Welcome, {username}</Typography>
        <SearchBar
          actionToDispatch={searchUsers}
          searchPlaceholder={'Search for user by username, Company or E-mail address'}
          store={users}
          customStyles={styles.searchBarContainer}
        />
      </Box>
      <Box sx={styles.body}>
        <Box sx={styles.optionsBox}>
          <SummariesBtn actionToDispatch={filterBySummary} store={users} />
          <Box sx={styles.sortAddBox}>
            <SortByList actionToDispatch={usersSortBy} store={users} />
            <OfoqThemeBtn name='Add Customer User' onClick={handleNavigation} />
          </Box>
        </Box>
        <Grid sx={styles.filterBox} item xs={12} sm={6} lg={12}>
          <TitleWithCount pageName='Users' store={users} />
          <FilterView setFilter={setFilter} filter={filter} customStyles={styles.filterBtn} />
        </Grid>
        {filter && clearFilter && (
          <Grid container justifyContent='flex-end'>
            <Grid item sx={{ marginBottom: '10px' }}>
              <Button
                onClick={handleClearFilter}
                sx={styles.filterIcon}>
                <ReplayRoundedIcon />
                <Typography sx={styles.clearFilter}>Clear Filter</Typography>
              </Button>
            </Grid>
          </Grid>
        )}
        {filter && (
          <Grid container spacing={3} sx={styles.listContainer} justifyContent='space-between'>
            <Grid item xs={12} sm={6} lg={6} sx={styles.filterAutoComplete}>
              <OfoqAutoComplete
                name={'User Type'}
                optionsConfig={users?.userTypes}
                defaultOption={filterUserType}
                refState={setFilterUserType}
                multiple

              />
            </Grid>
            <Grid item xs={12} sm={6} lg={6} sx={styles.filterAutoComplete}>
              <OfoqAutoComplete
                name={'Company'}
                optionsConfig={companies?.value}
                defaultOption={filterUserCompany}
                refState={setFilterUserCompany}
                multiple
              />
            </Grid>
          </Grid>
        )}
        <Divider />
        {users?.value?.map((user) => (
          <Box sx={styles.userListContainer} key={user.id}>
            <SysAdminUsersCard data={user} />
          </Box>
        ))}
        <LoadMore actionToDispatch={usersPagination} store={users} />
        <Loader store={users} />
      </Box>
    </Box>
  )
}

const styles = {
  container: {
    padding: { xs: '0 20px', sm: '0 70px' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  userListContainer: {
    display: 'flex',
    width: '100%',
  },
  welcomeHeader: {
    fontSize: { xs: '16px', sm: '24px' },
  },
  titleBox: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    width: '100%',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    svg: {
      fontSize: { xs: '20px', sm: 'unset' },
      marginTop: { xs: '10px', sm: 'unset' },
    },
  },
  searchBarContainer: {
    input: {
      fontSize: { xs: '14px', sm: 'unset' },
    },
  },
  optionsBox: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '2%',
    width: '100%',
  },
  sortAddBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: { xs: 'space-between', sm: 'flex-end' },
    width: '100%',
  },
  filterBox: {
    marginTop: { xs: '20px', sm: 5 },
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  filterBtn: {
    right: 0,
    borderRadius: { xs: 4, sm: 'unset' },
    '& svg': {
      width: { xs: '12px', sm: '16px' },
      height: { xs: '12px', sm: '16px' },
    },
  },
  body: {
    width: '100%',
  },
  filterIcon: {
    textTransform: 'capitalize',
    color: 'text.hint',
    '& svg': {
      width: { xs: '12px', sm: '16px' },
      height: { xs: '12px', sm: '16px' },
    },
  },
  clearFilter: {
    marginLeft: { xs: '5px', sm: '10px' },
    fontSize: { xs: '11px', sm: '16px' },
  },
  listContainer: {
    height: 'auto',
    padding: { xs: '0 10px 20px 10px', sm: '0 23px 30px 30px' },
    borderRadius: '12px',
    boxShadow: '0 1px 3px 0 #cfd8dc , 0 0 0 1px #f1f1f1',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    width: '100%',
    marginBottom: '1%',
  },
  filterAutoComplete: { alignSelf: 'baseline', paddingTop: '10px' },
}

export default Users
